// Checklist assembly — README.md "Checklist items". Buy, then Connect, then
// Set up, then Room, in that order.

import type { SpeakerEntry } from '@roomtreatment/core';
import type { ChannelGroup } from '@roomtreatment/av-catalog';
import type { BuildContext } from './context';
import type { Channel, ChecklistItem } from './types';
import { channelGroup } from './channels';
import { channelLower, countNoun, GROUP_LABEL, joinList, fmtOhms, upperFirst } from './copy';
import { runOrigin, runLength, wireGauge, wireRunTotals } from './runs';

type BuyCategory = 'front' | 'center' | 'surround' | 'surround_back' | 'height' | 'sub';

const BUY_CATEGORY_FOR_GROUP: Record<ChannelGroup, BuyCategory> = {
  front: 'front', center: 'center', surround: 'surround',
  surround_back: 'surround_back', height1: 'height', height2: 'height', height3: 'height', height4: 'height', wide: 'front',
};

const BUY_TEXT: Record<Exclude<BuyCategory, 'sub'>, string> = {
  front: 'Front speakers',
  center: 'Center speaker',
  surround: 'Surround speakers',
  surround_back: 'Rear surround speakers',
  height: 'Height speakers',
};

function isBound(entry: SpeakerEntry, ctx: BuildContext): boolean {
  return !!entry.productId && ctx.catalog.has(entry.productId);
}

function resolveSpeaker(ctx: BuildContext, id: string): SpeakerEntry | undefined {
  return ctx.doc.speakers.find((s) => s.id === id);
}

function buyItems(ctx: BuildContext): ChecklistItem[] {
  const items: ChecklistItem[] = [];
  const unboundByCategory = new Map<BuyCategory, number>();

  for (const id of [...ctx.bedIds, ...ctx.overheadIds]) {
    const entry = resolveSpeaker(ctx, id);
    if (!entry || isBound(entry, ctx)) continue;
    const group = channelGroup(ctx.channels[id]);
    if (!group) continue;
    const cat = BUY_CATEGORY_FOR_GROUP[group];
    unboundByCategory.set(cat, (unboundByCategory.get(cat) ?? 0) + 1);
  }
  let unboundSubs = 0;
  for (const id of ctx.subIds) {
    const entry = resolveSpeaker(ctx, id);
    if (entry && !isBound(entry, ctx)) unboundSubs++;
  }

  for (const cat of ['front', 'center', 'surround', 'surround_back', 'height'] as const) {
    if (unboundByCategory.has(cat)) items.push({ id: `buy:${cat}`, group: 'buy', text: BUY_TEXT[cat] });
  }
  if (unboundSubs > 0) {
    items.push({
      id: 'buy:sub',
      group: 'buy',
      text: unboundSubs > 1 ? `${unboundSubs} subwoofers` : 'Subwoofer',
    });
  }

  if (ctx.doc.speakers.length > 0 && !ctx.avUnit) {
    items.push({ id: 'buy:av', group: 'buy', text: 'AV receiver' });
  }

  if (ctx.assignment && ctx.assignment.noAmpChannel.length > 0) {
    const k = ctx.assignment.noAmpChannel.length;
    items.push({ id: 'buy:amp', group: 'buy', text: `${k}-channel amplifier` });
  }

  // Speaker wire — every channelled, non-active speaker (a generic speaker
  // counts as passive, same as AVR-03/04), regardless of whether an AV
  // unit exists yet (the wire is needed no matter which receiver is
  // eventually bought — Buy items are not gated on an AV unit the way
  // Connect/Set up are).
  const origin = runOrigin(ctx);
  const totalByGauge = wireRunTotals(ctx, [...ctx.bedIds, ...ctx.overheadIds]);
  for (const gauge of [16, 14, 12, 10] as const) {
    const total = totalByGauge.get(gauge);
    if (!total) continue;
    items.push({
      id: `buy:wire:${gauge}`,
      group: 'buy',
      text: `${gauge} AWG speaker wire, ${total.toFixed(1)} m`,
    });
  }

  // Subwoofer cable — needs a real AV unit as the endpoint.
  if (ctx.avUnit && ctx.subIds.length > 0) {
    const lens = ctx.subIds
      .map((id) => resolveSpeaker(ctx, id))
      .filter((e): e is SpeakerEntry => !!e)
      .map((e) => runLength(origin, e.position));
    const longest = Math.max(...lens);
    items.push({
      id: 'buy:sub-cable',
      group: 'buy',
      text: lens.length > 1
        ? `${lens.length} subwoofer cables, up to ${longest.toFixed(1)} m`
        : `Subwoofer cable, ${longest.toFixed(1)} m`,
    });
  }

  return items;
}

function connectItems(ctx: BuildContext): ChecklistItem[] {
  if (!ctx.avUnit || !ctx.assignment) return [];
  const items: ChecklistItem[] = [];
  const origin = runOrigin(ctx);
  const avName = productNameOf(ctx);

  const ampIndex = new Map<string, number>();
  ctx.amps.forEach((a, i) => ampIndex.set(a.gearId, i + 1));

  const order = [...ctx.bedIds, ...ctx.overheadIds];
  for (const id of order) {
    const a = ctx.assignment.bySpeaker.get(id);
    if (!a || !a.driving) continue;
    const entry = resolveSpeaker(ctx, id);
    if (!entry) continue;
    const channel = ctx.channels[id];
    const groupLabel = GROUP_LABEL[a.group];

    let text: string;
    if (a.driving.kind === 'internal') {
      const len = runLength(origin, entry.position);
      const z = a.product && a.product.specs.kind === 'speaker' ? a.product.specs.impedanceOhm?.v : undefined;
      const gauge = wireGauge(z, len);
      text = `${channelLower(channel)} → ${avName} ${groupLabel} terminals (${gauge} AWG, ${len.toFixed(1)} m)`;
    } else if (a.driving.kind === 'external') {
      const i = ampIndex.get(a.driving.gearId) ?? 0;
      text = `${channelLower(channel)} → amplifier ${i} channel ${a.driving.channelNumber}`;
    } else {
      // Active speaker fed line-level from the AV unit's pre-out. Not
      // explicitly templated in the README (which only covers terminal /
      // amplifier-channel speaker connections) — the closest fit reusing
      // the same group-label vocabulary, documented in README.md ambiguity
      // notes in the final report.
      text = `${channelLower(channel)} → ${avName} ${groupLabel} pre-outs`;
    }
    items.push({ id: `connect:${id}`, group: 'connect', text: upperFirst(text) });
  }

  // Amplifier inputs -> AV unit pre-outs, one per amp that actually
  // received a channel.
  const groupsPerAmp = new Map<string, Set<ChannelGroup>>();
  for (const a of ctx.assignment.bySpeaker.values()) {
    if (a.driving?.kind !== 'external') continue;
    const set = groupsPerAmp.get(a.driving.gearId) ?? new Set<ChannelGroup>();
    set.add(a.group);
    groupsPerAmp.set(a.driving.gearId, set);
  }
  const moveOrder: ChannelGroup[] = ['height2', 'height1', 'front', 'center', 'surround_back', 'surround'];
  for (const amp of ctx.amps) {
    const groups = groupsPerAmp.get(amp.gearId);
    if (!groups || groups.size === 0) continue;
    const i = ampIndex.get(amp.gearId) ?? 0;
    const labels = moveOrder.filter((g) => groups.has(g)).map((g) => GROUP_LABEL[g]);
    items.push({
      id: `connect:amp:${amp.gearId}`,
      group: 'connect',
      text: `Amplifier ${i} inputs → ${avName} ${joinList([...new Set(labels)])} pre-outs`,
    });
  }

  // Subwoofers.
  const subOuts = ctx.avUnit.specs.preouts.v.subOuts;
  if (subOuts > 0) {
    ctx.subIds.forEach((id, i) => {
      const entry = resolveSpeaker(ctx, id);
      if (!entry) return;
      const len = runLength(origin, entry.position);
      const n = (i % subOuts) + 1;
      items.push({
        id: `connect:${id}`,
        group: 'connect',
        text: `Subwoofer → ${avName} sub out ${n} (${len.toFixed(1)} m)`,
      });
    });
  }

  return items;
}

function setupItems(ctx: BuildContext): ChecklistItem[] {
  if (!ctx.avUnit || !ctx.assignment) return [];
  const items: ChecklistItem[] = [];

  const moveOrder: ChannelGroup[] = ['height2', 'height1', 'front', 'center', 'surround_back', 'surround'];
  const externalGroups = new Set<ChannelGroup>();
  for (const a of ctx.assignment.bySpeaker.values()) {
    if (a.driving?.kind === 'external') externalGroups.add(a.group);
  }
  if (externalGroups.size > 0) {
    const labels = [...new Set(moveOrder.filter((g) => externalGroups.has(g)).map((g) => GROUP_LABEL[g]))];
    items.push({
      id: 'setup:amp-assign',
      group: 'setup',
      text: `Assign ${joinList(labels)} to the external amplifier in speaker setup.`,
    });
  }

  const impedanceSettings = ctx.avUnit.specs.impedanceSettings?.v;
  if (impedanceSettings && impedanceSettings.length > 0) {
    let minZ: number | undefined;
    for (const a of ctx.assignment.bySpeaker.values()) {
      if (a.driving?.kind !== 'internal') continue;
      const z = a.product && a.product.specs.kind === 'speaker' ? a.product.specs.impedanceOhm?.v : undefined;
      if (z != null && (minZ == null || z < minZ)) minZ = z;
    }
    if (minZ != null && minZ < impedanceSettings[0]) {
      const opt = Math.max(...impedanceSettings.filter((v) => v <= minZ));
      items.push({ id: 'setup:impedance', group: 'setup', text: `Set speaker impedance to ${fmtOhms(opt)} Ω.` });
    }
  }

  if (ctx.subIds.length > 0) {
    items.push({ id: 'setup:crossover', group: 'setup', text: 'Set speakers to Small with an 80 Hz crossover.' });
  }

  const roomCorrection = ctx.avUnit.specs.roomCorrection?.v;
  items.push({
    id: 'setup:room-correction',
    group: 'setup',
    text: roomCorrection ? `Run ${roomCorrection}.` : 'Run room correction.',
  });

  return items;
}

function roomItems(ctx: BuildContext): ChecklistItem[] {
  if (ctx.opts.hasRoomResults) return [];
  return [{ id: 'room:compute', group: 'room', text: 'Run Compute to check the room.' }];
}

function productNameOf(ctx: BuildContext): string {
  const p = ctx.avUnit!.product;
  return `${p.brand} ${p.model}`;
}

export function buildChecklist(ctx: BuildContext): ChecklistItem[] {
  return [...buyItems(ctx), ...connectItems(ctx), ...setupItems(ctx), ...roomItems(ctx)];
}

// Re-exported for tests that want to check a single speaker's derived
// channel/group without importing channels.ts directly.
export type { Channel };
